import moment from 'moment';

// Engine output is UTC and stays UTC on this screen, so every instant is formatted with moment.utc
// and never through a local-time conversion.
const utcDateTimeFormat = "DD/MM/YYYY HH:mm:ss";
const utcDateFormat = "DD/MM/YYYY";

const unsetCharValue = "\u0000";
const noFiltersText = "None";
const yesText = "Yes";
const noText = "No";

const defaultArray = [];

function formatUtcDateTime(value) {
    if (value == null) {
        return "";
    }

    return moment.utc(value).format(utcDateTimeFormat);
}

function formatUtcDate(value) {
    if (value == null) {
        return "";
    }

    return moment.utc(value).format(utcDateFormat);
}

function formatBoolean(value) {
    if (value == null) {
        return "";
    }

    return value ? yesText : noText;
}

function formatValue(value) {
    if (value == null) {
        return "";
    }

    return `${value}`;
}

// The engine leaves an unset rest flag as the NUL character, which would otherwise render as a cell
// that looks empty but is not.
function formatRestFlag(value) {
    if (value === unsetCharValue) {
        return "";
    }

    return formatValue(value);
}

// The endpoint names every post-engine filter the row matches, not the one that would have reached
// it first, so the list is presented as matches rather than as a removal chain.
function formatMatchingFilters(filters) {
    if (filters == null || filters.length === 0) {
        return noFiltersText;
    }

    return filters.join(", ");
}

function infringementColumns() {
    return [
        { header: "Occurred at (UTC)", value: (outcome) => formatUtcDateTime(outcome.row?.occurredAt) },
        { header: "Code", value: (outcome) => formatValue(outcome.row?.code) },
        { header: "Sub code", value: (outcome) => formatValue(outcome.row?.subCode) },
        // The shared generator converts the times inside its text for display, so this is the one
        // column on the page that is not UTC. Its width makes the longest sentences wrap to about two
        // lines, which is what keeps a row of the set on one screen.
        { header: "Description (UK local)", value: (outcome) => formatValue(outcome.description), width: "880px" },
        // A parameter is a time for some infringement types and a count or duration for others, so the
        // header marks only the times as UTC rather than calling the whole column a UTC time.
        { header: "Parameter 1 (times UTC)", value: (outcome) => formatValue(outcome.row?.parameter1) },
        { header: "Parameter 2 (times UTC)", value: (outcome) => formatValue(outcome.row?.parameter2) },
        { header: "Parameter 3 (times UTC)", value: (outcome) => formatValue(outcome.row?.parameter3) },
        { header: "Parameter 4 (times UTC)", value: (outcome) => formatValue(outcome.row?.parameter4) },
        { header: "Parameter 5 (times UTC)", value: (outcome) => formatValue(outcome.row?.parameter5) },
        { header: "MSI", value: (outcome) => formatBoolean(outcome.row?.msi) },
        { header: "Matching filters", value: (outcome) => formatMatchingFilters(outcome.wouldBeRemovedBy) },
        { header: "Re-timed", value: (outcome) => formatBoolean(outcome.wasRetimed === true) }
    ];
}

const activityColumns = [
    { header: "Start (UTC)", value: (activity) => formatUtcDateTime(activity.startDateTime) },
    { header: "End (UTC)", value: (activity) => formatUtcDateTime(activity.endDateTime) },
    { header: "Duration (minutes)", value: (activity) => formatValue(activity.duration) },
    { header: "Activity type", value: (activity) => formatValue(activity.activityType) },
    { header: "Activity group", value: (activity) => formatValue(activity.activityGroup) },
    { header: "Comment", value: (activity) => formatValue(activity.comment) },
    { header: "Double manned", value: (activity) => formatBoolean(activity.doubleManned) },
    { header: "Domestic", value: (activity) => formatBoolean(activity.domestic) },
    { header: "PSV", value: (activity) => formatBoolean(activity.psv) },
    { header: "EU data", value: (activity) => formatBoolean(activity.euData) },
    { header: "Digital", value: (activity) => formatBoolean(activity.digital) },
    { header: "Manual", value: (activity) => formatBoolean(activity.manual) },
    { header: "NI rules", value: (activity) => formatBoolean(activity.niRules) },
    { header: "Analysis rules", value: (activity) => formatValue(activity.analysisRulesName) }
];

const restColumns = [
    { header: "Rest start (UTC)", value: (rest) => formatUtcDateTime(rest.restStart) },
    { header: "Rest end (UTC)", value: (rest) => formatUtcDateTime(rest.restEnd) },
    { header: "Duration (minutes)", value: (rest) => formatValue(rest.duration) },
    { header: "Daily rest", value: (rest) => formatRestFlag(rest.dailyRest) },
    { header: "Weekly rest", value: (rest) => formatRestFlag(rest.weeklyRest) },
    { header: "Week", value: (rest) => formatValue(rest.week) },
    { header: "Week start (UTC)", value: (rest) => formatUtcDateTime(rest.weekStart) },
    { header: "Balance", value: (rest) => formatValue(rest.balance) },
    { header: "Compensation", value: (rest) => formatValue(rest.compensation) },
    { header: "Compensates (UTC)", value: (rest) => formatUtcDateTime(rest.compensates) },
    { header: "Compensation provided by (UTC)", value: (rest) => formatUtcDateTime(rest.compensationProvidedBy) },
    { header: "Drive (minutes)", value: (rest) => formatValue(rest.drive) },
    { header: "Split", value: (rest) => formatValue(rest.split) },
    { header: "EU", value: (rest) => formatBoolean(rest.eu) },
    { header: "Daily rest not valid", value: (rest) => formatBoolean(rest.dailyRestNotValid) }
];

const workingTimeSummaryColumns = [
    { header: "Week commencing (UTC)", value: (week) => formatUtcDateTime(week.weekCommencing) },
    { header: "Week working time (minutes)", value: (week) => formatValue(week.weekWTTotal) },
    { header: "Period start week (UTC)", value: (week) => formatUtcDateTime(week.wtPeriodStartWeekCommencing) },
    { header: "Period end week (UTC)", value: (week) => formatUtcDateTime(week.wtPeriodEndWeekCommencing) },
    { header: "Period average (minutes)", value: (week) => formatValue(week.wtPeriodAverage) },
    { header: "Full period average (minutes)", value: (week) => formatValue(week.wtFullPeriodAverage) }
];

const awayFromHomeColumns = [
    { header: "From (UTC)", value: (period) => formatUtcDateTime(period.entryDateFrom) },
    { header: "To (UTC)", value: (period) => formatUtcDateTime(period.entryDateTo) }
];

const traceColumns = [
    { header: "Trace start (UTC)", value: (trace) => formatUtcDateTime(trace.traceStart) },
    { header: "First activity (UTC)", value: (trace) => formatUtcDateTime(trace.firstActivityStart) },
    { header: "Last activity end (UTC)", value: (trace) => formatUtcDateTime(trace.lastActivityEnd) },
    { header: "Activities", value: (trace) => formatValue(trace.activityCount) },
    { header: "Analysis rules", value: (trace) => formatValue(trace.analysisRulesName) },
    { header: "Digital", value: (trace) => formatBoolean(trace.digital) },
    { header: "Manual", value: (trace) => formatBoolean(trace.manual) },
    { header: "Faults", value: (trace) => formatValue(trace.faults) }
];

const workingTimePeriodColumns = [
    { header: "Start date (UTC)", value: (period) => formatUtcDate(period.startDate) },
    { header: "End date (UTC)", value: (period) => formatUtcDate(period.endDate) },
    { header: "Rolling weeks", value: (period) => formatValue(period.rollingWeeks) },
    { header: "Opted out of night work", value: (period) => formatValue(period.optOutOfNightWork) },
    { header: "Night work limit", value: (period) => formatValue(period.nightWorkLimit) }
];

/**
 * Turns a harness run into the sets the screen and the Excel export both render, so the workbook
 * can never drift from the columns on screen.
 */
export function buildHarnessResultSets(result) {
    return [
        {
            key: "infringements",
            title: "Infringements",
            sheetName: "Infringements",
            description: "Engine output before the post-engine filters run. Matching filters names every filter the row matches, not the one that would have removed it first.",
            columns: infringementColumns(),
            rows: result?.infringements ?? defaultArray
        },
        {
            key: "prePlanInfringements",
            title: "Pre-plan infringements",
            sheetName: "Pre-plan infringements",
            description: "Produced by a second pass over merged plan activity, so the activity behind these rows is not in the Activities set.",
            columns: infringementColumns(),
            rows: result?.prePlanInfringements ?? defaultArray
        },
        {
            key: "traces",
            title: "Traces",
            sheetName: "Traces",
            description: "Every trace the analysis read. The read reaches back before the start date so the engine has its context, so this covers more than the range you ran.",
            columns: traceColumns,
            rows: result?.traces ?? defaultArray
        },
        {
            key: "activityData",
            title: "Activities",
            sheetName: "Activities",
            description: "The sanitised activity the first pass analysed.",
            columns: activityColumns,
            rows: result?.activityData ?? defaultArray
        },
        {
            key: "tempActivityData",
            title: "EU input activities",
            sheetName: "EU input activities",
            description: "The sanitised activity with a leading rest and any leave or absence taken out, which is what the EU rules start from.",
            columns: activityColumns,
            rows: result?.tempActivityData ?? defaultArray
        },
        {
            key: "euActivityData",
            title: "EU activities",
            sheetName: "EU activities",
            description: "The activity the EU rules analysed.",
            columns: activityColumns,
            rows: result?.euActivityData ?? defaultArray
        },
        {
            key: "restSummary",
            title: "Rests",
            sheetName: "Rests",
            description: "The rest summary the engine calculated from the activity above.",
            columns: restColumns,
            rows: result?.restSummary ?? defaultArray
        },
        {
            key: "workingTimeActivity",
            title: "Working-time activities",
            sheetName: "Working time activities",
            description: "The activity the working-time rules analysed.",
            columns: activityColumns,
            rows: result?.workingTimeActivity ?? defaultArray
        },
        {
            key: "workingTimeSummary",
            title: "Working-time summary",
            sheetName: "Working time summary",
            description: "The engine's working time week by week, with the averages over the reference period it grouped the week into.",
            columns: workingTimeSummaryColumns,
            rows: result?.workingTimeSummary ?? defaultArray
        },
        {
            key: "workingTimePeriods",
            title: "Working-time periods",
            sheetName: "Working time periods",
            description: "The driver's configured working-time periods, not the periods the analysis grouped its weeks by - the working-time summary shows those.",
            columns: workingTimePeriodColumns,
            rows: result?.workingTimePeriods ?? defaultArray
        },
        {
            key: "awayFromHomePeriods",
            title: "Away from home",
            sheetName: "Away from home",
            description: "The periods the engine treated the driver as away from home.",
            columns: awayFromHomeColumns,
            rows: result?.awayFromHomePeriods ?? defaultArray
        }
    ];
}

export function buildHarnessSheets(resultSets) {
    return resultSets.map((resultSet) => {
        return {
            name: resultSet.sheetName,
            headers: resultSet.columns.map((column) => column.header),
            rows: resultSet.rows.map((row) => resultSet.columns.map((column) => column.value(row)))
        };
    });
}
